import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, MapPin, BedDouble, Maximize, Check, AlertTriangle } from "lucide-react";
import { priceLabel } from "@/lib/api";

export function MatchScore({ score, size = 64 }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const color = score >= 80 ? "hsl(43 74% 49%)" : score >= 60 ? "hsl(38 80% 55%)" : "hsl(215 16% 55%)";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="match-score">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(214 32% 91%)" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-bold leading-none" style={{ color, fontSize: size * 0.3 }}>{score}</span>
        <span className="text-[9px] text-slate-400 font-medium">MATCH</span>
      </div>
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-3 ${accent ? "bg-accent/15 text-accent" : "bg-secondary text-slate-600"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export function OwnerBadge({ owner, small }) {
  if (!owner) return null;
  return (
    <div className="flex items-center gap-2">
      <img src={owner.avatar} alt="" className={`${small ? "h-6 w-6" : "h-8 w-8"} rounded-full object-cover`} />
      <div className="leading-tight">
        <div className={`${small ? "text-xs" : "text-sm"} font-medium text-slate-700 flex items-center gap-1`}>
          {owner.name}{owner.verified && <BadgeCheck className="h-3.5 w-3.5 text-success" />}
        </div>
        <div className="text-[11px] text-slate-500">{owner.agency}</div>
      </div>
    </div>
  );
}

export function Reasons({ match }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(match.reasons || []).slice(0, 4).map((r, i) => (
        <span key={i} className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md">
          <Check className="h-3 w-3" />{r}
        </span>
      ))}
      {(match.concerns || []).slice(0, 2).map((r, i) => (
        <span key={`c${i}`} className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 px-2 py-1 rounded-md">
          <AlertTriangle className="h-3 w-3" />{r}
        </span>
      ))}
    </div>
  );
}

export function PropertyMeta({ p }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
      <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{p.neighborhood}, {p.city}</span>
      {p.bedrooms > 0 && <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" />{p.bedrooms} BR</span>}
      {p.floor_area_sqm > 0 && <span className="flex items-center gap-1"><Maximize className="h-4 w-4" />{p.floor_area_sqm} sqm</span>}
    </div>
  );
}

export { priceLabel };

export function Fade({ children, delay = 0, className = "" }) {
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}
